import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import { MapPin, Star, Navigation, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

// Custom marker icon
const createCustomIcon = (isActive = false) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div class="custom-marker-icon ${isActive ? "active" : ""}"></div>`,
    iconSize: isActive ? [40, 40] : [32, 32],
    iconAnchor: isActive ? [20, 40] : [16, 32],
    popupAnchor: [0, -32],
  });
};

// Component to fit map to markers
const FitBoundsToMarkers = ({ beaches }) => {
  const map = useMap();

  useEffect(() => {
    if (beaches && beaches.length > 0) {
      const validBeaches = beaches.filter(
        (b) => b.latitude && b.longitude
      );
      if (validBeaches.length > 0) {
        const bounds = L.latLngBounds(
          validBeaches.map((b) => [b.latitude, b.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [beaches, map]);

  return null;
};

const BeachMap = ({
  beaches = [],
  center = [39.8283, -98.5795], // Center of US
  zoom = 4,
  selectedBeach = null,
  onBeachSelect = () => {},
  fitBounds = true,
  className = "",
}) => {
  const handleDirections = (beach) => {
    if (beach.latitude && beach.longitude) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${beach.latitude},${beach.longitude}`,
        "_blank"
      );
    }
  };

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      className={`w-full h-full rounded-xl ${className}`}
      data-testid="beach-map"
      scrollWheelZoom={true}
      style={{ minHeight: "400px" }}
    >
      <TileLayer
        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
      />
      
      {fitBounds && <FitBoundsToMarkers beaches={beaches} />}

      {beaches.map((beach) => {
        if (!beach.latitude || !beach.longitude) return null;
        
        const isSelected = selectedBeach?.id === beach.id;
        
        return (
          <Marker
            key={beach.id}
            position={[beach.latitude, beach.longitude]}
            icon={createCustomIcon(isSelected)}
            eventHandlers={{
              click: () => onBeachSelect(beach),
            }}
          >
            <Popup>
              <div className="p-3 min-w-[220px]" data-testid={`map-popup-${beach.slug}`}>
                <h3 className="font-semibold text-[#3D3D3D] font-['Outfit'] mb-1">
                  {beach.name}
                </h3>
                <div className="flex items-center gap-1 text-sm text-[#6B6B6B] mb-2">
                  <MapPin className="w-3 h-3" />
                  {beach.city}, {beach.state}
                </div>
                {beach.average_rating && (
                  <div className="flex items-center gap-1 mb-3">
                    <Star className="w-4 h-4 fill-[#CBBD93] text-[#CBBD93]" />
                    <span className="text-sm font-medium">
                      {beach.average_rating.toFixed(1)}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Link to={`/beach/${beach.slug}`} className="flex-1">
                    <Button size="sm" variant="outline" className="w-full text-xs">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Details
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    className="bg-[#3D3D3D] text-white text-xs"
                    onClick={() => handleDirections(beach)}
                  >
                    <Navigation className="w-3 h-3 mr-1" />
                    Go
                  </Button>
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
};

export default BeachMap;
